import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { clientProfilesAPI, materialTypesAPI, piecesRequisesAPI, financingAPI, cmsSettingsAPI, categoriesAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Package, FileText, X, GripVertical, Loader2, Banknote, Building, Save, Tag, Wrench, AlertTriangle, CheckCircle } from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('company');
  
  // Client Profiles State
  const [profiles, setProfiles] = useState([]);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: '',
    code: '',
    description: '',
    required_documents: [],
    color: '#3b82f6'
  });
  
  // Material Types State
  const [materialTypes, setMaterialTypes] = useState([]);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({
    name: '',
    code: '',
    description: '',
    icon: 'Package',
    custom_fields: []
  });
  const [newField, setNewField] = useState({ name: '', key: '', field_type: 'text', unit: '', show_on_pdf: true });
  
  // Pieces Requises State
  const [pieces, setPieces] = useState([]);
  
  // Financing Document Configs State
  const [financingConfigs, setFinancingConfigs] = useState([]);
  const [financingConfigDialogOpen, setFinancingConfigDialogOpen] = useState(false);
  const [editingFinancingConfig, setEditingFinancingConfig] = useState(null);
  const [financingConfigForm, setFinancingConfigForm] = useState({
    client_type: '',
    documents: []
  });
  const [newDocName, setNewDocName] = useState('');
  const [newDocRequired, setNewDocRequired] = useState(true);
  
  // Company Settings State
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    company_legal_form: '',
    company_siret: '',
    company_rcs: '',
    company_tva: '',
    company_capital: '',
    contact_phone: '',
    contact_phone_2: '',
    contact_email: '',
    contact_address: '',
    contact_address_line2: '',
    contact_postal_code: '',
    contact_city: '',
    contact_country: 'France',
    contact_hours: '',
    facebook_url: '',
    instagram_url: '',
    youtube_url: '',
    linkedin_url: '',
  });
  const [savingCompany, setSavingCompany] = useState(false);
  
  // Option Categories State
  const [optionCategories, setOptionCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ id: '', name: '', icon: 'box', order: 50 });
  
  const [loading, setLoading] = useState(false);
  
  // Migration State
  const [orphanStats, setOrphanStats] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profilesRes, typesRes, piecesRes, financingRes, settingsRes, categoriesRes] = await Promise.all([
        clientProfilesAPI.getAll(),
        materialTypesAPI.getAll(),
        piecesRequisesAPI.getAll(),
        financingAPI.getDocConfigs(),
        cmsSettingsAPI.get(),
        categoriesAPI.getAll()
      ]);
      setProfiles(profilesRes.data);
      setMaterialTypes(typesRes.data);
      setPieces(piecesRes.data);
      setFinancingConfigs(financingRes.data || []);
      setOptionCategories(categoriesRes.data || []);
      
      // Set company settings
      if (settingsRes.data) {
        setCompanySettings({
          company_name: settingsRes.data.company_name || '',
          company_legal_form: settingsRes.data.company_legal_form || '',
          company_siret: settingsRes.data.company_siret || '',
          company_rcs: settingsRes.data.company_rcs || '',
          company_tva: settingsRes.data.company_tva || '',
          company_capital: settingsRes.data.company_capital || '',
          contact_phone: settingsRes.data.contact_phone || '',
          contact_phone_2: settingsRes.data.contact_phone_2 || '',
          contact_email: settingsRes.data.contact_email || '',
          contact_address: settingsRes.data.contact_address || '',
          contact_address_line2: settingsRes.data.contact_address_line2 || '',
          contact_postal_code: settingsRes.data.contact_postal_code || '',
          contact_city: settingsRes.data.contact_city || '',
          contact_country: settingsRes.data.contact_country || 'France',
          contact_hours: settingsRes.data.contact_hours || '',
          facebook_url: settingsRes.data.facebook_url || '',
          instagram_url: settingsRes.data.instagram_url || '',
          youtube_url: settingsRes.data.youtube_url || '',
          linkedin_url: settingsRes.data.linkedin_url || '',
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // ============= CLIENT PROFILES =============
  
  const handleSaveProfile = async () => {
    if (!profileForm.name || !profileForm.code) {
      toast.error('Le nom et le code sont requis');
      return;
    }
    
    setLoading(true);
    try {
      if (editingProfile) {
        await clientProfilesAPI.update(editingProfile.id, profileForm);
        toast.success('Profil mis à jour');
      } else {
        await clientProfilesAPI.create(profileForm);
        toast.success('Profil créé');
      }
      setProfileDialogOpen(false);
      setEditingProfile(null);
      setProfileForm({ name: '', code: '', description: '', required_documents: [], color: '#3b82f6' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setProfileForm({
      name: profile.name,
      code: profile.code,
      description: profile.description || '',
      required_documents: profile.required_documents || [],
      color: profile.color || '#3b82f6'
    });
    setProfileDialogOpen(true);
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Supprimer ce profil ?')) return;
    try {
      await clientProfilesAPI.delete(profileId);
      toast.success('Profil supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleDocument = (docName) => {
    setProfileForm(prev => ({
      ...prev,
      required_documents: prev.required_documents.includes(docName)
        ? prev.required_documents.filter(d => d !== docName)
        : [...prev.required_documents, docName]
    }));
  };

  // ============= MATERIAL TYPES =============
  
  const handleSaveType = async () => {
    if (!typeForm.name || !typeForm.code) {
      toast.error('Le nom et le code sont requis');
      return;
    }
    
    setLoading(true);
    try {
      if (editingType) {
        await materialTypesAPI.update(editingType.id, typeForm);
        toast.success('Type de matériel mis à jour');
      } else {
        await materialTypesAPI.create(typeForm);
        toast.success('Type de matériel créé');
      }
      setTypeDialogOpen(false);
      setEditingType(null);
      setTypeForm({ name: '', code: '', description: '', icon: 'Package', custom_fields: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setTypeForm({
      name: type.name,
      code: type.code,
      description: type.description || '',
      icon: type.icon || 'Package',
      custom_fields: type.custom_fields || []
    });
    setTypeDialogOpen(true);
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Supprimer ce type de matériel ?')) return;
    try {
      await materialTypesAPI.delete(typeId);
      toast.success('Type supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addCustomField = () => {
    if (!newField.name || !newField.key) {
      toast.error('Nom et clé requis pour le champ');
      return;
    }
    setTypeForm(prev => ({
      ...prev,
      custom_fields: [...prev.custom_fields, { ...newField }]
    }));
    setNewField({ name: '', key: '', field_type: 'text', unit: '', show_on_pdf: true });
  };

  const removeCustomField = (index) => {
    setTypeForm(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, i) => i !== index)
    }));
  };

  // ============= FINANCING DOCUMENT CONFIGS =============
  
  const handleEditFinancingConfig = (config) => {
    setEditingFinancingConfig(config);
    setFinancingConfigForm({
      client_type: config.client_type,
      documents: config.documents || []
    });
    setFinancingConfigDialogOpen(true);
  };

  const handleSaveFinancingConfig = async () => {
    if (!financingConfigForm.client_type) {
      toast.error('Le type de client est requis');
      return;
    }
    
    setLoading(true);
    try {
      await financingAPI.updateDocConfig(financingConfigForm.client_type, {
        client_type: financingConfigForm.client_type,
        documents: financingConfigForm.documents
      });
      toast.success('Configuration mise à jour');
      setFinancingConfigDialogOpen(false);
      setEditingFinancingConfig(null);
      setFinancingConfigForm({ client_type: '', documents: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const addFinancingDoc = () => {
    if (!newDocName.trim()) {
      toast.error('Le nom du document est requis');
      return;
    }
    setFinancingConfigForm(prev => ({
      ...prev,
      documents: [...prev.documents, { name: newDocName.trim(), required: newDocRequired }]
    }));
    setNewDocName('');
    setNewDocRequired(true);
  };

  const removeFinancingDoc = (index) => {
    setFinancingConfigForm(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // ============= COMPANY SETTINGS =============
  
  const handleSaveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      await cmsSettingsAPI.update(companySettings);
      toast.success('Coordonnées de la société mises à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingCompany(false);
    }
  };

  // ============= OPTION CATEGORIES =============
  
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Le nom de la catégorie est requis');
      return;
    }
    
    setLoading(true);
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, categoryForm);
        toast.success('Catégorie mise à jour');
      } else {
        const id = categoryForm.id || categoryForm.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        await categoriesAPI.create({ ...categoryForm, id });
        toast.success('Catégorie créée');
      }
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      setCategoryForm({ id: '', name: '', icon: 'box', order: 50 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    
    try {
      await categoriesAPI.delete(categoryId);
      toast.success('Catégorie supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };
  
  const handleInitDefaultCategories = async () => {
    try {
      await categoriesAPI.initDefaults();
      toast.success('Catégories par défaut initialisées');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // ============= MIGRATION FUNCTIONS =============
  
  const fetchOrphanStats = async () => {
    setLoadingStats(true);
    try {
      const response = await api.get('/admin/orphan-devis-stats');
      setOrphanStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
      console.error('Error fetching orphan stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleMigrateOrphanDevis = async () => {
    if (!window.confirm('Cette action va lier tous les devis orphelins aux opportunités.\n\nVoulez-vous continuer ?')) {
      return;
    }
    
    setMigrating(true);
    try {
      const response = await api.post('/admin/migrate-orphan-devis');
      toast.success(response.data.message);
      setOrphanStats(null);
      fetchOrphanStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la migration');
      console.error('Error migrating:', error);
    } finally {
      setMigrating(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-slate-700">Accès réservé aux administrateurs</h2>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">Paramètres</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-7 lg:w-[1050px]">
          <TabsTrigger value="company" className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            <span className="hidden sm:inline">Société</span>
            <span className="sm:hidden">Sté</span>
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            <span className="hidden sm:inline">Catégories</span>
            <span className="sm:hidden">Cat.</span>
          </TabsTrigger>
          <TabsTrigger value="profiles" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Profils</span>
            <span className="sm:hidden">Prof.</span>
          </TabsTrigger>
          <TabsTrigger value="materials" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Matériel</span>
            <span className="sm:hidden">Mat.</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Documents</span>
            <span className="sm:hidden">Docs</span>
          </TabsTrigger>
          <TabsTrigger value="financing" className="flex items-center gap-2">
            <Banknote className="w-4 h-4" />
            <span className="hidden sm:inline">Financement</span>
            <span className="sm:hidden">Fin.</span>
          </TabsTrigger>
          <TabsTrigger value="tools" className="flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            <span className="hidden sm:inline">Outils</span>
            <span className="sm:hidden">Out.</span>
          </TabsTrigger>
        </TabsList>

        {/* ============= SOCIÉTÉ TAB ============= */}
        <TabsContent value="company" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Coordonnées de la Société</CardTitle>
              <CardDescription>Informations légales et coordonnées de contact</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Informations légales */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Informations légales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label>Nom de la société</Label>
                    <Input
                      value={companySettings.company_name}
                      onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
                      placeholder="IDMAT OI"
                    />
                  </div>
                  <div>
                    <Label>Forme juridique</Label>
                    <Input
                      value={companySettings.company_legal_form}
                      onChange={(e) => setCompanySettings({...companySettings, company_legal_form: e.target.value})}
                      placeholder="SAS, SARL, etc."
                    />
                  </div>
                  <div>
                    <Label>SIRET</Label>
                    <Input
                      value={companySettings.company_siret}
                      onChange={(e) => setCompanySettings({...companySettings, company_siret: e.target.value})}
                      placeholder="XXX XXX XXX XXXXX"
                    />
                  </div>
                  <div>
                    <Label>RCS</Label>
                    <Input
                      value={companySettings.company_rcs}
                      onChange={(e) => setCompanySettings({...companySettings, company_rcs: e.target.value})}
                      placeholder="RCS Saint-Denis"
                    />
                  </div>
                  <div>
                    <Label>N° TVA Intracommunautaire</Label>
                    <Input
                      value={companySettings.company_tva}
                      onChange={(e) => setCompanySettings({...companySettings, company_tva: e.target.value})}
                      placeholder="FR XX XXXXXXXXX"
                    />
                  </div>
                  <div>
                    <Label>Capital social</Label>
                    <Input
                      value={companySettings.company_capital}
                      onChange={(e) => setCompanySettings({...companySettings, company_capital: e.target.value})}
                      placeholder="10 000 €"
                    />
                  </div>
                </div>
              </div>

              {/* Coordonnées */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">📍 Adresse</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Adresse</Label>
                    <Input
                      value={companySettings.contact_address}
                      onChange={(e) => setCompanySettings({...companySettings, contact_address: e.target.value})}
                      placeholder="Numéro et rue"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Complément d'adresse</Label>
                    <Input
                      value={companySettings.contact_address_line2}
                      onChange={(e) => setCompanySettings({...companySettings, contact_address_line2: e.target.value})}
                      placeholder="Bâtiment, étage, etc."
                    />
                  </div>
                  <div>
                    <Label>Code postal</Label>
                    <Input
                      value={companySettings.contact_postal_code}
                      onChange={(e) => setCompanySettings({...companySettings, contact_postal_code: e.target.value})}
                      placeholder="97400"
                    />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input
                      value={companySettings.contact_city}
                      onChange={(e) => setCompanySettings({...companySettings, contact_city: e.target.value})}
                      placeholder="Saint-Denis"
                    />
                  </div>
                  <div>
                    <Label>Pays</Label>
                    <Input
                      value={companySettings.contact_country}
                      onChange={(e) => setCompanySettings({...companySettings, contact_country: e.target.value})}
                      placeholder="France"
                    />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">📞 Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Téléphone principal</Label>
                    <Input
                      value={companySettings.contact_phone}
                      onChange={(e) => setCompanySettings({...companySettings, contact_phone: e.target.value})}
                      placeholder="0693 52 64 49"
                    />
                  </div>
                  <div>
                    <Label>Téléphone secondaire</Label>
                    <Input
                      value={companySettings.contact_phone_2}
                      onChange={(e) => setCompanySettings({...companySettings, contact_phone_2: e.target.value})}
                      placeholder="Optionnel"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={companySettings.contact_email}
                      onChange={(e) => setCompanySettings({...companySettings, contact_email: e.target.value})}
                      placeholder="contact@idmatoi.com"
                    />
                  </div>
                  <div>
                    <Label>Horaires d'ouverture</Label>
                    <Input
                      value={companySettings.contact_hours}
                      onChange={(e) => setCompanySettings({...companySettings, contact_hours: e.target.value})}
                      placeholder="Lun-Ven: 8h-17h"
                    />
                  </div>
                </div>
              </div>

              {/* Réseaux sociaux */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">🌐 Réseaux sociaux</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Facebook</Label>
                    <Input
                      value={companySettings.facebook_url}
                      onChange={(e) => setCompanySettings({...companySettings, facebook_url: e.target.value})}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div>
                    <Label>Instagram</Label>
                    <Input
                      value={companySettings.instagram_url}
                      onChange={(e) => setCompanySettings({...companySettings, instagram_url: e.target.value})}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div>
                    <Label>YouTube</Label>
                    <Input
                      value={companySettings.youtube_url}
                      onChange={(e) => setCompanySettings({...companySettings, youtube_url: e.target.value})}
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                  <div>
                    <Label>LinkedIn</Label>
                    <Input
                      value={companySettings.linkedin_url}
                      onChange={(e) => setCompanySettings({...companySettings, linkedin_url: e.target.value})}
                      placeholder="https://linkedin.com/..."
                    />
                  </div>
                </div>
              </div>

              {/* Bouton de sauvegarde */}
              <div className="border-t pt-6 flex justify-end">
                <Button 
                  onClick={handleSaveCompanySettings} 
                  disabled={savingCompany}
                  className="bg-yellow-500 hover:bg-yellow-600"
                >
                  {savingCompany ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= CATÉGORIES D'OPTIONS TAB ============= */}
        <TabsContent value="categories" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Catégories d'Options</CardTitle>
                <CardDescription>Gérez les catégories pour organiser les options et accessoires</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleInitDefaultCategories}>
                  Réinitialiser défauts
                </Button>
                <Dialog open={categoryDialogOpen} onOpenChange={(open) => {
                  setCategoryDialogOpen(open);
                  if (!open) {
                    setEditingCategory(null);
                    setCategoryForm({ id: '', name: '', icon: 'box', order: 50 });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-yellow-500 hover:bg-yellow-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouvelle catégorie
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>{editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Nom *</Label>
                        <Input
                          value={categoryForm.name}
                          onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                          placeholder="Ex: Équipements de levage"
                        />
                      </div>
                      <div>
                        <Label>Identifiant (auto-généré si vide)</Label>
                        <Input
                          value={categoryForm.id}
                          onChange={(e) => setCategoryForm({ ...categoryForm, id: e.target.value })}
                          placeholder="Ex: levage"
                          disabled={!!editingCategory}
                        />
                      </div>
                      <div>
                        <Label>Icône</Label>
                        <Select value={categoryForm.icon} onValueChange={(v) => setCategoryForm({ ...categoryForm, icon: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="box">📦 Box</SelectItem>
                            <SelectItem value="bucket">🪣 Godet</SelectItem>
                            <SelectItem value="tracks">🔗 Chenilles</SelectItem>
                            <SelectItem value="link">⛓️ Attache</SelectItem>
                            <SelectItem value="droplet">💧 Hydraulique</SelectItem>
                            <SelectItem value="home">🏠 Cabine</SelectItem>
                            <SelectItem value="shield">🛡️ Sécurité</SelectItem>
                            <SelectItem value="wrench">🔧 Outil</SelectItem>
                            <SelectItem value="cog">⚙️ Engrenage</SelectItem>
                            <SelectItem value="plus">➕ Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Ordre d'affichage</Label>
                        <Input
                          type="number"
                          value={categoryForm.order}
                          onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 50 })}
                          min="1"
                          max="100"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Annuler</Button>
                      <Button onClick={handleSaveCategory} disabled={loading} className="bg-yellow-500 hover:bg-yellow-600">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {editingCategory ? 'Modifier' : 'Créer'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {optionCategories.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Tag className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucune catégorie créée</p>
                  <Button variant="link" onClick={handleInitDefaultCategories}>
                    Initialiser les catégories par défaut
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {optionCategories.map((cat) => (
                    <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">
                          {cat.icon === 'bucket' && '🪣'}
                          {cat.icon === 'tracks' && '🔗'}
                          {cat.icon === 'link' && '⛓️'}
                          {cat.icon === 'droplet' && '💧'}
                          {cat.icon === 'home' && '🏠'}
                          {cat.icon === 'shield' && '🛡️'}
                          {cat.icon === 'wrench' && '🔧'}
                          {cat.icon === 'cog' && '⚙️'}
                          {cat.icon === 'plus' && '➕'}
                          {cat.icon === 'box' && '📦'}
                          {!['bucket', 'tracks', 'link', 'droplet', 'home', 'shield', 'wrench', 'cog', 'plus', 'box'].includes(cat.icon) && '📦'}
                        </span>
                        <div>
                          <p className="font-medium text-slate-800">{cat.name}</p>
                          <p className="text-xs text-slate-500">ID: {cat.id} | Ordre: {cat.order}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingCategory(cat);
                            setCategoryForm({ id: cat.id, name: cat.name, icon: cat.icon, order: cat.order });
                            setCategoryDialogOpen(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteCategory(cat.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= PROFILS CLIENTS TAB ============= */}
        <TabsContent value="profiles" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Profils Contacts</CardTitle>
                <CardDescription>Définissez les profils clients et leurs documents requis pour la SNC</CardDescription>
              </div>
              <Dialog open={profileDialogOpen} onOpenChange={(open) => {
                setProfileDialogOpen(open);
                if (!open) {
                  setEditingProfile(null);
                  setProfileForm({ name: '', code: '', description: '', required_documents: [], color: '#3b82f6' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-yellow-500 hover:bg-yellow-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau profil
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingProfile ? 'Modifier le profil' : 'Nouveau profil client'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nom du profil *</Label>
                        <Input
                          value={profileForm.name}
                          onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                          placeholder="Ex: Agriculteur"
                        />
                      </div>
                      <div>
                        <Label>Code *</Label>
                        <Input
                          value={profileForm.code}
                          onChange={(e) => setProfileForm({ ...profileForm, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                          placeholder="Ex: agriculteur"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={profileForm.description}
                        onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                        placeholder="Description du profil"
                      />
                    </div>
                    <div>
                      <Label>Couleur</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={profileForm.color}
                          onChange={(e) => setProfileForm({ ...profileForm, color: e.target.value })}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <span className="text-sm text-slate-500">{profileForm.color}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="mb-2 block">Documents requis pour ce profil (SNC)</Label>
                      <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3">
                        {pieces.length === 0 ? (
                          <p className="text-sm text-slate-500">Aucun document configuré. Ajoutez-en dans l'onglet Documents.</p>
                        ) : (
                          pieces.map((piece) => (
                            <div key={piece.id} className="flex items-center gap-2">
                              <Checkbox
                                checked={profileForm.required_documents.includes(piece.name)}
                                onCheckedChange={() => toggleDocument(piece.name)}
                              />
                              <span className="text-sm">{piece.name}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setProfileDialogOpen(false)}>Annuler</Button>
                    <Button onClick={handleSaveProfile} disabled={loading} className="bg-yellow-500 hover:bg-yellow-600">
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {editingProfile ? 'Mettre à jour' : 'Créer'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {profiles.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Aucun profil configuré</p>
              ) : (
                <div className="space-y-3">
                  {profiles.map((profile) => (
                    <div key={profile.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: profile.color || '#3b82f6' }}
                        />
                        <div>
                          <p className="font-medium">{profile.name}</p>
                          <p className="text-sm text-slate-500">
                            Code: {profile.code} • {profile.required_documents?.length || 0} documents requis
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEditProfile(profile)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteProfile(profile.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= TYPES MATERIEL TAB ============= */}
        <TabsContent value="materials" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Types de Matériel</CardTitle>
                <CardDescription>Définissez les types de matériel et leurs champs personnalisés</CardDescription>
              </div>
              <Dialog open={typeDialogOpen} onOpenChange={(open) => {
                setTypeDialogOpen(open);
                if (!open) {
                  setEditingType(null);
                  setTypeForm({ name: '', code: '', description: '', icon: 'Package', custom_fields: [] });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-yellow-500 hover:bg-yellow-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau type
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingType ? 'Modifier le type' : 'Nouveau type de matériel'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nom du type *</Label>
                        <Input
                          value={typeForm.name}
                          onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                          placeholder="Ex: Mini pelle"
                        />
                      </div>
                      <div>
                        <Label>Code *</Label>
                        <Input
                          value={typeForm.code}
                          onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                          placeholder="Ex: mini_pelle"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={typeForm.description}
                        onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                        placeholder="Description du type"
                      />
                    </div>
                    
                    {/* Custom Fields Section */}
                    <div className="border-t pt-4">
                      <Label className="text-base font-semibold mb-3 block">Champs personnalisés</Label>
                      <p className="text-sm text-slate-500 mb-3">Ces champs apparaîtront sur la fiche produit et le PDF</p>
                      
                      {/* Existing Fields */}
                      {typeForm.custom_fields.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {typeForm.custom_fields.map((field, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                              <GripVertical className="w-4 h-4 text-slate-400" />
                              <span className="flex-1 text-sm font-medium">{field.name}</span>
                              <span className="text-xs text-slate-500">{field.key}</span>
                              <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">{field.field_type}</span>
                              {field.unit && <span className="text-xs text-slate-500">{field.unit}</span>}
                              {field.show_on_pdf && <span className="text-xs text-green-600">PDF</span>}
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeCustomField(index)}>
                                <X className="w-3 h-3 text-red-500" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add New Field */}
                      <div className="grid grid-cols-6 gap-2 items-end">
                        <div className="col-span-2">
                          <Label className="text-xs">Nom du champ</Label>
                          <Input
                            value={newField.name}
                            onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                            placeholder="Ex: Profondeur"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Clé</Label>
                          <Input
                            value={newField.key}
                            onChange={(e) => setNewField({ ...newField, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                            placeholder="profondeur"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Type</Label>
                          <Select value={newField.field_type} onValueChange={(v) => setNewField({ ...newField, field_type: v })}>
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Texte</SelectItem>
                              <SelectItem value="number">Nombre</SelectItem>
                              <SelectItem value="select">Liste</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Unité</Label>
                          <Input
                            value={newField.unit}
                            onChange={(e) => setNewField({ ...newField, unit: e.target.value })}
                            placeholder="mm"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Button type="button" size="sm" onClick={addCustomField} className="w-full h-8">
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Checkbox
                          checked={newField.show_on_pdf}
                          onCheckedChange={(checked) => setNewField({ ...newField, show_on_pdf: checked })}
                        />
                        <span className="text-xs text-slate-600">Afficher sur le PDF</span>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>Annuler</Button>
                    <Button onClick={handleSaveType} disabled={loading} className="bg-yellow-500 hover:bg-yellow-600">
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {editingType ? 'Mettre à jour' : 'Créer'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {materialTypes.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Aucun type de matériel configuré</p>
              ) : (
                <div className="space-y-3">
                  {materialTypes.map((type) => (
                    <div key={type.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5 text-slate-600" />
                        <div>
                          <p className="font-medium">{type.name}</p>
                          <p className="text-sm text-slate-500">
                            Code: {type.code} • {type.custom_fields?.length || 0} champs personnalisés
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEditType(type)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteType(type.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= DOCUMENTS TAB ============= */}
        <TabsContent value="documents" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Documents (Pièces requises)</CardTitle>
              <CardDescription>Liste des documents pouvant être associés aux profils clients</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-slate-500 text-center py-4">
                Gérez les documents requis depuis la page "Pièces Requises" dans le menu.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= FINANCING DOCUMENTS TAB ============= */}
        <TabsContent value="financing" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Pièces de Financement</CardTitle>
              <CardDescription>Configurez les pièces justificatives requises pour les demandes de financement par type de client</CardDescription>
            </CardHeader>
            <CardContent>
              {profiles.length === 0 ? (
                <div className="text-center py-8">
                  <Banknote className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">Aucun profil client trouvé.</p>
                  <p className="text-sm text-slate-400">Créez d'abord des profils clients dans l'onglet "Profils Contacts" pour définir leurs pièces de financement.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Show all profiles and their financing configs */}
                  {profiles.map((profile) => {
                    const config = financingConfigs.find(c => c.client_type === profile.code);
                    return (
                      <div key={profile.id} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between p-4 bg-slate-50">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: profile.color || '#3b82f6' }}
                            />
                            <div>
                              <p className="font-medium">{profile.name}</p>
                              <p className="text-sm text-slate-500">
                                {config?.documents?.length || 0} pièce(s) configurée(s)
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditFinancingConfig({
                              client_type: profile.code,
                              documents: config?.documents || []
                            })}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Configurer
                          </Button>
                        </div>
                        {config?.documents && config.documents.length > 0 && (
                          <div className="p-4 bg-white border-t">
                            <div className="flex flex-wrap gap-2">
                              {config.documents.map((doc, idx) => (
                                <span 
                                  key={idx} 
                                  className={`text-xs px-2 py-1 rounded ${doc.required ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`}
                                >
                                  {doc.name} {doc.required && '*'}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financing Config Dialog */}
          <Dialog open={financingConfigDialogOpen} onOpenChange={(open) => {
            setFinancingConfigDialogOpen(open);
            if (!open) {
              setEditingFinancingConfig(null);
              setFinancingConfigForm({ client_type: '', documents: [] });
              setNewDocName('');
              setNewDocRequired(true);
            }
          }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Pièces de financement - {editingFinancingConfig?.client_type}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-slate-500">
                  Définissez les pièces justificatives requises pour les demandes de financement 
                  de type "{editingFinancingConfig?.client_type}".
                </p>
                
                {/* Current documents list */}
                {financingConfigForm.documents.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Pièces configurées</Label>
                    {financingConfigForm.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded border">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{doc.name}</span>
                          {doc.required && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Obligatoire</span>
                          )}
                        </div>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFinancingDoc(index)}>
                          <X className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new document */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium mb-2 block">Ajouter une pièce</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newDocName}
                      onChange={(e) => setNewDocName(e.target.value)}
                      placeholder="Nom du document (ex: Carte d'identité)"
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFinancingDoc())}
                    />
                    <div className="flex items-center gap-2 px-2 border rounded-md bg-white">
                      <Checkbox
                        checked={newDocRequired}
                        onCheckedChange={setNewDocRequired}
                      />
                      <span className="text-xs text-slate-600 whitespace-nowrap">Obligatoire</span>
                    </div>
                    <Button type="button" size="sm" onClick={addFinancingDoc}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setFinancingConfigDialogOpen(false)}>Annuler</Button>
                <Button onClick={handleSaveFinancingConfig} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Enregistrer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ============= OUTILS TAB ============= */}
        <TabsContent value="tools" className="mt-6">
          <div className="space-y-6">
            {/* Migration Devis Orphelins */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Migration Devis → Opportunités
                </CardTitle>
                <CardDescription>
                  Lier les anciens devis qui ne sont pas encore associés à une opportunité.
                  Cette opération est sécurisée et peut être exécutée plusieurs fois.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats Display */}
                {orphanStats ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-slate-800">{orphanStats.total_devis}</div>
                      <div className="text-sm text-slate-500">Total devis</div>
                    </div>
                    <div className={`rounded-lg p-4 text-center ${orphanStats.orphan_count > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                      <div className={`text-2xl font-bold ${orphanStats.orphan_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {orphanStats.orphan_count}
                      </div>
                      <div className="text-sm text-slate-500">Devis orphelins</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{orphanStats.linked_count}</div>
                      <div className="text-sm text-slate-500">Devis liés</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{orphanStats.percent_linked}%</div>
                      <div className="text-sm text-slate-500">Couverture</div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-lg p-6 text-center text-slate-500">
                    Cliquez sur "Vérifier" pour analyser les devis orphelins
                  </div>
                )}

                {/* Orphan Examples */}
                {orphanStats && orphanStats.orphan_count > 0 && orphanStats.orphan_examples && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700">
                      Exemples de devis orphelins ({Math.min(orphanStats.orphan_examples.length, 10)} sur {orphanStats.orphan_count})
                    </div>
                    <div className="divide-y max-h-60 overflow-y-auto">
                      {orphanStats.orphan_examples.slice(0, 10).map((d, idx) => (
                        <div key={idx} className="px-4 py-2 flex items-center justify-between text-sm">
                          <span className="font-medium text-slate-800">{d.reference || 'N/A'}</span>
                          <span className="text-slate-500">{d.client_name || 'Client inconnu'}</span>
                          {d.total && <span className="text-green-600 font-medium">{d.total.toLocaleString('fr-FR')} €</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {orphanStats && orphanStats.orphan_count === 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-green-800">Tous les devis sont liés à une opportunité !</p>
                      <p className="text-sm text-green-600">Aucune migration nécessaire.</p>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={fetchOrphanStats}
                    disabled={loadingStats}
                  >
                    {loadingStats && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Vérifier les devis orphelins
                  </Button>
                  {orphanStats && orphanStats.orphan_count > 0 && (
                    <Button 
                      onClick={handleMigrateOrphanDevis}
                      disabled={migrating}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      {migrating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Migrer {orphanStats.orphan_count} devis orphelins
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
